import { useEffect, useState } from 'react';
import { Plus, Search, Edit3, Trash2, X, Building2, Globe, Phone, Mail, Activity } from 'lucide-react';
import { crmApi, EmpresaConta, Contato } from '../crmApi';
import EtiquetasPicker from '../EtiquetasPicker';
import CamposCustomForm from '../CamposCustomForm';
import ResponsavelPicker from '../ResponsavelPicker';
import CardDrawer from '../CardDrawer';

interface Props { session: any; }

const PORTES: { value: NonNullable<EmpresaConta['porte']>; label: string }[] = [
  { value: '', label: '—' },
  { value: 'micro', label: 'Microempresa' },
  { value: 'pequena', label: 'Pequena' },
  { value: 'media', label: 'Média' },
  { value: 'grande', label: 'Grande' },
];

const RAMOS = [
  'Tecnologia', 'Comércio', 'Serviços', 'Indústria', 'Agronegócio',
  'Construção', 'Educação', 'Saúde', 'Financeiro', 'Varejo', 'Logística', 'Outro'
];

const CRMEmpresasConta = ({ session }: Props) => {
  const [empresas, setEmpresas] = useState<EmpresaConta[]>([]);
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [busca, setBusca] = useState('');
  const [filtroRamo, setFiltroRamo] = useState('');
  const [minhaCarteira, setMinhaCarteira] = useState(true);
  const [carregando, setCarregando] = useState(false);
  const [editando, setEditando] = useState<EmpresaConta | null>(null);
  const [drawerEmpresa, setDrawerEmpresa] = useState<EmpresaConta | null>(null);
  const [novo, setNovo] = useState(false);

  const carregar = async () => {
    setCarregando(true);
    try {
      const [emp, cts] = await Promise.all([
        crmApi.listarEmpresasConta(session),
        crmApi.listarContatos(session).catch(() => []),
      ]);
      setEmpresas(emp);
      setContatos(cts);
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setCarregando(false); }
  };
  useEffect(() => { carregar(); }, []); // eslint-disable-line

  const meuEmail = session?.usuario?.email || '';
  const filtradas = empresas.filter(e => {
    const q = busca.toLowerCase().trim();
    const matchQ = !q ||
      e.razaoSocial.toLowerCase().includes(q) ||
      (e.nomeFantasia || '').toLowerCase().includes(q) ||
      (e.cnpj || '').includes(q) ||
      (e.email || '').toLowerCase().includes(q);
    const matchR = !filtroRamo || e.ramo === filtroRamo;
    const matchC = !minhaCarteira || (e.responsavelId === meuEmail);
    return matchQ && matchR && matchC;
  });

  const countContatos = (empresaId: string) => contatos.filter(c => c.empresaContaId === empresaId).length;

  const remover = async (e: EmpresaConta) => {
    if (!confirm(`Excluir empresa "${e.razaoSocial}"?\n\nObs.: contatos vinculados não serão excluídos, apenas perderão o vínculo com esta empresa.`)) return;
    await crmApi.removerEmpresaConta(session, e.id);
    carregar();
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-6 w-6" /> Empresas (Contas)
          </h1>
          <p className="text-sm text-gray-600">Cadastro central de organizações — clientes, prospects e parceiros corporativos</p>
        </div>
        <button onClick={() => setNovo(true)} className="bg-black text-white px-3 py-2 rounded-lg flex items-center gap-1.5 hover:bg-gray-800 text-sm">
          <Plus className="h-4 w-4" /> Nova Empresa
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por razão social, fantasia, CNPJ, email…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <select value={filtroRamo} onChange={e => setFiltroRamo(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white">
          <option value="">Todos os ramos</option>
          {RAMOS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          <button onClick={() => setMinhaCarteira(true)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${minhaCarteira ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'}`}>
            Minha carteira
          </button>
          <button onClick={() => setMinhaCarteira(false)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${!minhaCarteira ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'}`}>
            Toda a equipe
          </button>
        </div>
      </div>

      {carregando && <div className="text-sm text-gray-500">Carregando…</div>}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-xs text-gray-600 uppercase">
              <th className="text-left px-4 py-2">Razão Social</th>
              <th className="text-left px-4 py-2 hidden md:table-cell">CNPJ / Ramo</th>
              <th className="text-left px-4 py-2 hidden sm:table-cell">Contato</th>
              <th className="text-left px-4 py-2">Contatos</th>
              <th className="px-4 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {filtradas.length === 0 && !carregando && (
              <tr><td colSpan={5} className="text-center py-12 text-gray-500 text-sm">Nenhuma empresa cadastrada.</td></tr>
            )}
            {filtradas.map(e => (
              <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-2">
                  <button onClick={() => setEditando(e)} className="font-medium text-gray-900 hover:underline text-left">
                    {e.razaoSocial}
                  </button>
                  {e.nomeFantasia && <div className="text-[11px] text-gray-500">{e.nomeFantasia}</div>}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700 hidden md:table-cell">
                  {e.cnpj && <div className="text-xs">{e.cnpj}</div>}
                  {e.ramo && <div className="text-[11px] text-gray-500">{e.ramo}</div>}
                </td>
                <td className="px-4 py-2 text-xs text-gray-600 hidden sm:table-cell">
                  {e.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3" /> {e.email}</div>}
                  {e.telefone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" /> {e.telefone}</div>}
                  {e.site && <div className="flex items-center gap-1"><Globe className="h-3 w-3" /> {e.site}</div>}
                </td>
                <td className="px-4 py-2">
                  <span className="text-[11px] px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full">
                    {countContatos(e.id)} contato{countContatos(e.id) !== 1 ? 's' : ''}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => setDrawerEmpresa(e)} title="Atividades"
                    className="p-1 hover:bg-blue-50 rounded text-blue-700 mr-1"><Activity className="h-4 w-4" /></button>
                  <button onClick={() => setEditando(e)} className="p-1 hover:bg-gray-100 rounded text-gray-600"><Edit3 className="h-4 w-4" /></button>
                  <button onClick={() => remover(e)} className="p-1 hover:bg-red-50 rounded text-red-600 ml-1"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(novo || editando) && (
        <EmpresaContaModal session={session} empresa={editando || undefined}
          onClose={() => { setNovo(false); setEditando(null); }}
          onSaved={() => { setNovo(false); setEditando(null); carregar(); }} />
      )}

      <CardDrawer session={session} open={!!drawerEmpresa} onClose={() => setDrawerEmpresa(null)}
        vinculo={drawerEmpresa ? { empresaContaId: drawerEmpresa.id } : {}}
        titulo={drawerEmpresa?.razaoSocial || ''}
        subtitulo={drawerEmpresa?.nomeFantasia || drawerEmpresa?.cnpj || ''}
        telefone={drawerEmpresa?.telefone}
        responsavelId={drawerEmpresa?.responsavelId} />
    </div>
  );
};

const EmpresaContaModal = ({ session, empresa, onClose, onSaved }: { session: any; empresa?: EmpresaConta; onClose: () => void; onSaved: () => void }) => {
  const [razaoSocial, setRazaoSocial] = useState(empresa?.razaoSocial || '');
  const [nomeFantasia, setNomeFantasia] = useState(empresa?.nomeFantasia || '');
  const [cnpj, setCnpj] = useState(empresa?.cnpj || '');
  const [site, setSite] = useState(empresa?.site || '');
  const [email, setEmail] = useState(empresa?.email || '');
  const [telefone, setTelefone] = useState(empresa?.telefone || '');
  const [ramo, setRamo] = useState(empresa?.ramo || '');
  const [porte, setPorte] = useState<EmpresaConta['porte']>(empresa?.porte || '');
  const [responsavelId, setResponsavelId] = useState<string | null>(empresa?.responsavelId || (session?.usuario?.email || null));
  const [observacoes, setObservacoes] = useState(empresa?.observacoes || '');
  const [etiquetas, setEtiquetas] = useState<string[]>(empresa?.etiquetas || []);
  const [camposCustom, setCamposCustom] = useState<Record<string, any>>(empresa?.camposCustom || {});
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    if (!razaoSocial.trim()) { alert('Razão social obrigatória'); return; }
    setSalvando(true);
    try {
      const payload = { razaoSocial: razaoSocial.trim(), nomeFantasia, cnpj, site, email, telefone, ramo, porte, responsavelId, observacoes, etiquetas, camposCustom };
      if (empresa) await crmApi.atualizarEmpresaConta(session, empresa.id, payload);
      else await crmApi.criarEmpresaConta(session, payload);
      onSaved();
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setSalvando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Building2 className="h-4 w-4" /> {empresa ? 'Editar Empresa' : 'Nova Empresa'}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs text-gray-600">Razão Social *</label>
              <input value={razaoSocial} onChange={e => setRazaoSocial(e.target.value)} autoFocus
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Nome Fantasia</label>
              <input value={nomeFantasia} onChange={e => setNomeFantasia(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-600">CNPJ</label>
              <input value={cnpj} onChange={e => setCnpj(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-600">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Telefone</label>
              <input value={telefone} onChange={e => setTelefone(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Site</label>
              <input value={site} onChange={e => setSite(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">Ramo</label>
              <select value={ramo} onChange={e => setRamo(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                <option value="">—</option>
                {RAMOS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-600">Porte</label>
              <select value={porte || ''} onChange={e => setPorte(e.target.value as EmpresaConta['porte'])}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                {PORTES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>

          <ResponsavelPicker session={session} value={responsavelId} onChange={setResponsavelId} />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">Observações</label>
              <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} rows={2}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <EtiquetasPicker session={session} value={etiquetas} onChange={setEtiquetas} />
            </div>
          </div>

          <CamposCustomForm session={session} entidade="empresa-conta" value={camposCustom} onChange={setCamposCustom} />
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-700">Cancelar</button>
          <button onClick={salvar} disabled={salvando}
            className="px-4 py-1.5 bg-black text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-50">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CRMEmpresasConta;
